import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    // 1. Initialize layout based on URL parameters (Menu vs Sub-menu)
    this.#initLayoutFromNavigation();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Checks the URL for a 'view' parameter to decide which layout to show.
   * Main Menu links should have: ?view=main
   * Sub-menu links should have: ?view=sub
   */
  #initLayoutFromNavigation() {
    const urlParams = new URLSearchParams(window.location.search);
    const viewType = urlParams.get('view');
    const isDesktop = mediaQueryLarge.matches;

    if (!viewType) return;

    let targetValue = 'default';

    if (viewType === 'main') {
      // Logic: "Second grid layout option"
      // Desktop: 'zoom-out' | Mobile: 'default' (2-column)
      targetValue = isDesktop ? 'zoom-out' : 'default';
    } else if (viewType === 'sub') {
      // Logic: "First default grid layout option"
      // Desktop: 'default' | Mobile: 'mobile-single' (1-column)
      targetValue = isDesktop ? 'default' : 'mobile-single';
    }

    // Sync the Radio Input UI
    const inputSelector = isDesktop 
      ? `input[name="grid"][value="${targetValue}"]` 
      : `input[name="grid-mobile"][value="${targetValue}"]`;
    
    const targetInput = this.querySelector(inputSelector);
    if (targetInput) {
      // Uncheck others and check the target
      const groupName = isDesktop ? 'grid' : 'grid-mobile';
      this.querySelectorAll(`input[name="${groupName}"]`).forEach(i => i.checked = false);
      targetInput.checked = true;
    }

    // Apply the layout to the grid
    this.#setLayout(targetValue);
  }

  /**
   * Updates the layout via event (clicking the radio buttons manually)
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout with View Transition API support.
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Directly sets the data attribute on the grid.
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}